/* eslint-disable camelcase */

import { Component, Inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { NotificationService } from '@core/services/notifications.service';

import { ApiResponseModel as ApiResponse } from '@shared/models/services/api.response.model';

interface ConferencingSource {
    id: number;
    type: string;
    status: string;
    status_message: string;
    email: string;
    avatar: string;
    name: string;
    disconnecting: boolean;
}

@Component({
    selector: 'sfx-meeting-tool-settings',
    templateUrl: './meeting-tool-settings.component.html',
    styleUrls: ['./meeting-tool-settings.component.scss']
})
export class MeetingToolSettingsComponent {

    public readonly canAccessMeetingAI: boolean;

    public conferencingSources: ConferencingSource[] = [];
    public conferencingSourcesLoaded = false;

    private conferencingConnectInfo: { [key: string]: any } = {};

    public readonly conferencingTypeConfig: { [key: string]: { name: string, avatar: string } } = {
        google: { name: 'Google Meet', avatar: 'https://lib.salesflare.com/providers/google_meet.png' },
        zoom: { name: 'Zoom', avatar: 'https://lib.salesflare.com/providers/zoom.png' },
        office365: { name: 'Microsoft Teams', avatar: 'https://lib.salesflare.com/providers/microsoft_teams.png' }
    };

    constructor(
        @Inject('conferenceSyncProgressService') public conferenceSyncProgressService: any,
        @Inject('datasourceService') public datasourceService: any,

        @Inject('dialogService') private dialogService: any,

        @Inject('config') public config: any,
        @Inject('exceptionHandler') public exceptionHandler: any,
        @Inject('helperFunctionsService') public helperFunctionsService: any,
        @Inject('modelService') public modelService: any,

        @Inject('utilsService') public utilsService: any,
        @Inject('windowService') public windowService: any,

        public notificationService: NotificationService,
        private translate: TranslateService
    ) {
        this.canAccessMeetingAI = !!this.modelService.me?.flags?.find((flag: any) => flag.name === 'meetingAI')?.state;

        if (!this.canAccessMeetingAI) {
            return;
        }

        this.fetchConferencingSources();

        if (this.windowService.isMobile) {
            this.datasourceService.getConferencingConnectInfo()
                .then((response: ApiResponse<any>) => {

                    this.conferencingConnectInfo = response.data;
                });
        }
    }

    public fetchConferencingSources = () => {

        this.datasourceService.getConferencingSources()
            .then((response: ApiResponse<ConferencingSource[]>) => {
                const conferencingSources = response.data.map((source: ConferencingSource) => {

                    const typeConfig = this.conferencingTypeConfig[source.type] || {
                        name: source.type,
                        avatar: `https://lib.salesflare.com/providers/${source.type.toLowerCase()}.png`
                    };

                    return {
                        ...source,
                        name: typeConfig.name,
                        avatar: typeConfig.avatar
                    };
                });

                this.conferencingSources = conferencingSources;
                this.conferencingSourcesLoaded = true;
            });
    }

    private onConferencingConnected = () => {

        this.conferenceSyncProgressService.startPolling();
        this.notificationService.success(this.translate.instant('Great! Your meeting tool is connected.'));
        this.fetchConferencingSources();
    }

    private showAuthPopup = (popUpUrl: string, target: string) => {

        if (this.config.mode === 'outlook-web') {
            return this.helperFunctionsService.handleOutlookPluginOAuthFlow(popUpUrl);
        }

        return this.utilsService.popup(popUpUrl, target);
    }

    public connectGoogleMeet = () => {

        if (!this.windowService.isMobile) {

            let url = `${this.config.apiUrl}datasources/connect/conferencing?type=google`;

            if (this.modelService.authStrategy === 'bearer') {
                url += '&access_token=' + this.modelService.getToken();
            }

            return this.showAuthPopup(url, '')
                .then((queryParams: any) => {

                    if (queryParams && queryParams.get('success') !== 'true') {
                        if (queryParams.get('error') === 'UNAUTHORIZED_SCOPES') {

                            const insufficientPermissionsDialog = this.dialogService.confirm({ multiple: true })
                                .clickOutsideToClose(true)
                                .escapeToClose(true)
                                .title(this.translate.instant('Please enable all permissions'))
                                .textContent(this.translate.instant('Salesflare needs all the requested permissions to be able to automate your CRM work.'))
                                .ok(this.translate.instant('Enable'))
                                .cancel(this.translate.instant('cancel'));

                            return this.dialogService.show(insufficientPermissionsDialog)
                                .then(() => {

                                    this.connectGoogleMeet();
                                });
                        }
                    }

                    if (queryParams && queryParams.get('success') === 'true') {
                        this.onConferencingConnected();
                    }
                });
        }

        return this.windowService.plugins.googleplus.logout((msg: string) => {

            if (msg !== 'Logged user out' && msg !== 'logged out') {
                this.exceptionHandler(msg);
                this.notificationService.error('Oops! Something went wrong. Please try again.');
            }

            return this.mobileGoogleMeetLogin();
        }, (err: string) => {

            if (err !== 'Please use login or trySilentLogin before logging out') {
                this.exceptionHandler(err);
                this.notificationService.error('Oops! Something went wrong. Please try again.');
            }

            return this.mobileGoogleMeetLogin();
        });
    }

    public connectMicrosoftTeams = () => {

        return this.connectConferencingWithOAuth('office365', this.connectMicrosoftTeams);
    }

    public connectZoom = () => {

        return this.connectConferencingWithOAuth('zoom', this.connectZoom);
    }

    /**
     * Runs the server side OAuth flow for a conferencing provider.
     * On mobile the flow opens in the system browser and the server redirects back to the app via `salesflare://closeme.html`.
     *
     * @param {'office365' | 'zoom'} type
     * @param {Function} retry Called when the user agrees to retry after not granting all scopes
     * @returns {Promise<void>}
     */
    private connectConferencingWithOAuth = (type: 'office365' | 'zoom', retry: () => void) => {

        let url = `${this.config.apiUrl}datasources/connect/conferencing?type=${type}`;

        if (this.modelService.authStrategy === 'bearer') {
            url += '&access_token=' + this.modelService.getToken();
        }

        if (this.windowService.isMobile) {
            url += '&mobile=true';
        }

        return this.showAuthPopup(url, this.windowService.isMobile ? '_system' : '')
            .then((queryParams: any) => {

                if (queryParams && queryParams.get('success') !== 'true') {
                    if (queryParams.get('error') === 'UNAUTHORIZED_SCOPES') {

                        const insufficientPermissionsDialog = this.dialogService.confirm({ multiple: true })
                            .clickOutsideToClose(true)
                            .escapeToClose(true)
                            .title(this.translate.instant('Please enable all permissions'))
                            .textContent(this.translate.instant('Salesflare needs all the requested permissions to be able to automate your CRM work.'))
                            .ok(this.translate.instant('Enable'))
                            .cancel(this.translate.instant('cancel'));

                        return this.dialogService.show(insufficientPermissionsDialog)
                            .then(() => {

                                retry();
                            });
                    }
                }

                if (queryParams && queryParams.get('success') === 'true') {
                    this.onConferencingConnected();
                }
            });
    }

    private mobileGoogleMeetLogin = () => {

        return this.windowService.plugins.googleplus.login(this.conferencingConnectInfo, (googleAuthData: any) => {

            return this.datasourceService.tradeConferencing(googleAuthData, { noToast: true, noToastForStatusCode: [403] })
                .then(() => {

                    this.fetchConferencingSources();
                })
                .catch((err: any) => {

                    if (err?.data?.message === 'UNAUTHORIZED_SCOPES') {

                        const insufficientPermissionsDialog = this.dialogService.confirm({ multiple: true })
                            .clickOutsideToClose(true)
                            .escapeToClose(true)
                            .title(this.translate.instant('Please enable all permissions'))
                            .textContent(this.translate.instant('Salesflare needs all the requested permissions to be able to automate your CRM work.'))
                            .ok(this.translate.instant('Enable'))
                            .cancel(this.translate.instant('cancel'));

                        return this.dialogService.show(insufficientPermissionsDialog)
                            .then(() => {

                                this.connectGoogleMeet();
                            });
                    }

                    return this.windowService.plugins.googleplus.logout((msg: string) => {

                        if (msg !== 'Logged user out') {
                            this.exceptionHandler(msg);
                            this.notificationService.error(this.translate.instant('Oops! Something went wrong. Please try again.'));
                        }
                    });
                });
        }, (msg: string | number) => {

            if (msg && msg !== 12501) {
                this.exceptionHandler(msg);
                this.notificationService.error('Oops! Something went wrong. Please try again.');
            }

            return this.windowService.plugins.googleplus.logout((message: string) => {

                if (message !== 'Logged user out') {
                    this.exceptionHandler(message);
                    this.notificationService.error('Oops! Something went wrong. Please try again.');
                }
            });
        });
    }

    public disconnectConferencing = (source: ConferencingSource) => {

        source.disconnecting = true;

        this.datasourceService.deleteConferencingSource(source.id, source.type).then(() => {

            this.notificationService.success(this.translate.instant('Disconnected'));

            this.fetchConferencingSources();
        }).catch(() => {

            source.disconnecting = false;
        });
    }
}
